Woolman.Utils.prepareQueryParams();
Woolman.Collection = document.querySelector('[data-ajax-parent]');

// Constants
const STORE = 'ps';
const SDD = 'SDD';
const SDD_FILTER = 'sdd-filter';
const CC = 'CC';
const CC_FILTER = 'cc-filter';
const DELIVERY_METHOD_ENDPOINT = `${window.location.origin}/a/shipping/delivery-method/eligibility`;

/**
 * Generates search params from form data for filtering collection or search results.
 * @param {HTMLElement} formElement - Form element to turn into search params.
 * @returns
 */
const generateSearchParamsFromFormData = (formElement) => {
    const checkboxesToKeep = {};
    for (const element of formElement.elements) {
        const key = element.getAttribute('name');
        if (!key || !key.includes('filter') || (key === "delivery-filter-options" && !element.checked)) continue;
        const value = element.value;
        const inputType = element.getAttribute('type');

        if (value === '') continue;

        if (inputType == 'number' && (element.hasAttribute('data-min') || element.hasAttribute('data-max'))) {
            if (!element.closest('price-range').hasAttribute('data-updated')) {
                continue;
            }
        }

        if (inputType == 'checkbox') {
            if (element.checked) {
                if (!checkboxesToKeep[key] && key !== 'filter.v.m.custom.stores') {
                    checkboxesToKeep[key] = [value];
                    continue;
                } else {
                    if (key === 'filter.v.m.custom.stores' && !(key in checkboxesToKeep)) checkboxesToKeep[key] = [];
                    checkboxesToKeep[key].push(value);
                    continue;
                }
            }

            if (Shopify.queryParams[key] && !checkboxesToKeep[key]) {
                delete Shopify.queryParams[key];
            }
            continue;
        } else {
            Shopify.queryParams[key] = value;
        }
    }

    if (Object.entries(checkboxesToKeep).length > 0) {
        for (const [key, value] of Object.entries(checkboxesToKeep)) {
            Shopify.queryParams[key] = value.join(',');
        }
    }

    return Shopify.queryParams;
};

/**
 * Toggles available delivery filter options.
 * Determines if the delivery options should be displayed based on the provided `hideDeliveryMethods` flag.
 **/
const toggleDeliveryFilter = (hideDeliveryMethods = true, showErrorMessage = false) => {
    const errorMessageDisplay = hideDeliveryMethods ? 'block' : 'none';

    if (showErrorMessage) document.querySelector('#delivery-filter-error').style.display = errorMessageDisplay;

    // Toggle visibility of the delivery filters.
    if (!hideDeliveryMethods) {
        document.querySelector(`#delivery-filter-options`).classList.remove('hiding');
    } else {
        document.querySelector(`#delivery-filter-options`).classList.add('hiding');
    }

    if (hideDeliveryMethods) {
        // Hide all stores
        document.querySelectorAll('.delivery-filter-values:is([style*="display:block"])').forEach(store => {
            store.style.display = 'none'
        })
    }
}

/**
 * Makes a request to get stores eligible for either SDD or CC for the given postcode.
 */
const fetchDeliveryStores = async (selectedFilter, postcode) => {
    const deliveryType = selectedFilter === SDD_FILTER ? SDD : CC;

    const response = await fetch(`${DELIVERY_METHOD_ENDPOINT}?postcode=${postcode}&delivery_type=${deliveryType}&store=${STORE}`);

    return (await response.json())?.data ?? [];
}

/**
 * Handles visibility toggle of delivery methods.
 *
 * @param deliveryFilter
 * @param displayError
 */
const handleDeliveryErrorMessage = (deliveryFilter, displayError = true) => {
    if (![CC_FILTER, SDD_FILTER].includes(deliveryFilter)) return;

    // If the delivery filter is not available then don't proceed with the next events.
    if (!document.querySelector(`#${deliveryFilter}-input`)) return;

    document.querySelector(`#${deliveryFilter}-error`).style.display = displayError ? 'block' : 'none';

    if (displayError) {
        document.querySelector(`#delivery-filter-options`).classList.add('hiding');
    } else {
        document.querySelector(`#delivery-filter-options`).classList.remove('hiding');
    }
}

/**
 * Handles the selection process of the delivery filter option.
 * If SDD is selected then makes an API call to fetch the eligible stores and auto selects the store for collection page filtering.
 * However, for CC filter option, displays the selected filter but doesn't auto select it for the user.
 *
 * @param selectedFilter
 * @param postcode
 * @return {Promise<*[]>}
 */
const processDeliveryFilter = async (selectedFilter, postcode) => {
    const stores = [];

    // Add the loading GIF
    document.querySelector(`#loading-icon .loading-overlay`).classList.remove('hidden');

    (await fetchDeliveryStores(selectedFilter, postcode)).forEach(store => {
        // For SDD, auto-select the store option but for CC just make it visible.
        switch (selectedFilter) {
            case SDD_FILTER:
                const deliveryOption = document.querySelector(`#Filter-Delivery-${store}`);
                if (!deliveryOption) return;
                deliveryOption.checked = true;
                toggleDeliveryStoreLabel(store, false);
                break;
            default:
                toggleDeliveryStoreLabel(store);
        }

        stores.push(store);
    });

    // Remove the loading GIF
    document.querySelector(`#loading-icon .loading-overlay`).classList.add('hidden');

    return stores;
}

/**
 * Toggle store label visibility on the delivery filter.
 * @param store_number
 * @param enabled
 */
const toggleDeliveryStoreLabel = (store_number, enabled = true) => {
    const deliveryOptionDiv = document.querySelector(`#Filter-Container-Delivery-${store_number}`);
    if (!deliveryOptionDiv) return;
    deliveryOptionDiv.style.display = enabled ? "flex" : "none";
}

/**
 * Unchecks all selected stores for the delivery filter.
 */
const resetStoresSelection = (display = true) => {
    document.querySelectorAll('.delivery-store').forEach(availableStore => {
        availableStore.checked = false;
        if (!display) availableStore.style.display = "none";
    });

    if (!display) return;

    document.querySelectorAll('.filter-v-m-custom-stores').forEach(store => {
        store.style.display = "none";
    });
}

/**
 * This function fetches fresh collection page via AJAX request to collection's url.
 * @param {Array} selectors - Array of HTML element selectors to update.
 */
const getCollectionAJAX = async (selectors) => {
    Shopify.queryParams.page = 1;
    if (selectors) {
        try {
            const url = location.pathname + '?' + getCollectionURL();
            const res = await fetch(url);
            const body = await res.text();

            const parser = new DOMParser();
            const doc = parser.parseFromString(body, 'text/html');

            for (const selector of selectors) {
                if (!document.querySelector(selector) || !doc.querySelector(selector)) continue;
                document.querySelector(selector).innerHTML = doc.querySelector(selector).innerHTML;
            }

            const historyStateUrl = location.pathname + '?' + getCollectionURL();
            window.history.replaceState('', '', historyStateUrl);
            Woolman.Collection.classList.remove('processing');
        } catch (err) {
            location.search = getCollectionURL();
        }
    } else {
        location.search = getCollectionURL();
    }
};

/**
 * Returns URL for current collection. Utilizes URL parameters to save user's filters, sorting and pagination.
 * @param {string} view - Template name to load with view attribute, e.g. 'ajax'
 * @returns
 */
const getCollectionURL = (view) => {
    Shopify.queryParams.view = view || undefined;
    const urlToReturn = new URLSearchParams(JSON.parse(JSON.stringify(Shopify.queryParams))).toString();
    return decodeURIComponent(urlToReturn);
};

class SortBySelect extends HTMLElement {
    constructor() {
        'use strict';
        super();

        this.el = this.querySelector('select');
        this.selectors = {
            grid: '#product-grid-ajax',
            pagination: '#pagination-ajax',
        };

        if (Shopify.queryParams['sort_by']) {
            this.el.value = Shopify.queryParams['sort_by'];
        }

        this.el.addEventListener('change', this.onSelectChange.bind(this));
    }

    async onSelectChange(event) {
        Woolman.Collection.classList.add('processing');
        Woolman.Utils.prepareQueryParams();
        const value = event.target.value;
        Shopify.queryParams.sort_by = value;
        getCollectionAJAX(Object.values(this.selectors));
    }
}

if (customElements.get('sort-by') === undefined) {
    customElements.define('sort-by', SortBySelect);
}

class FacetsForm extends HTMLElement {
    #postcode = '';
    #appliedDeliveryFilter = '';

    constructor() {
        'use strict';
        super();

        this.el = this.querySelector('form');
        this.selectors = {
            grid: '#product-grid-ajax',
            count: '#products-count-ajax',
            currentFacets: '#current-facets-ajax',
            pagination: '#pagination-ajax',
        };
        const inputs = this.el.querySelectorAll('input');
        document.addEventListener('DOMContentLoaded', debounce(this.deliveryFilterOnReload.bind(this), 150));
        inputs.forEach((input) => {
            if (input.name === 'postcode-field') {
                this.addEventListener('keyup', debounce(this.onPostcodeEntry.bind(this), 500));
                return;
            } else if (input.name === 'delivery-filter-options') {
                input.addEventListener('click', debounce(this.onShippingMethodSelection.bind(this), 500));
                return;
            }

            input.addEventListener('change', debounce(this.onCheckboxChanged.bind(this), 150));
        });

        this.el.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    /**
     * Set delivery filter values retrieved from the URL parameter.
     * This is used to fill the current delivery filter value to the page on page reload.
     */
    async deliveryFilterOnReload() {
        if (!location.href.includes('?') || location.href.split('?').length < 2) return;

        const params = new URLSearchParams(location.href.split('?')[1]);

        if (!params.size) {
            this.#postcode = '';

            return;
        }

        // If postcode information is not available then don't process the selection of the delivery type.
        if (!params.get('postcode-field') || isNaN(Number(this.#postcode))) {
            this.#postcode = '';
            return;
        }

        // Append the available postcode to the UI input field.
        this.el.querySelector('input[name="postcode-field"]').value = params.get('postcode-field');
        // The delivery filter is not set yet, so the postcode entry method will only deal with making sure that the given postcode is valid.
        await this.onPostcodeEntry(false);

        // After selection of the postcode data, continue with the delivery filter selection process.
        // If any of the available delivery filter option is selected.
        if (!params.get('delivery-filter-options')) {
            this.#appliedDeliveryFilter = '';
            return;
        }

        // Handle filter delivery option selection.
        toggleDeliveryFilter(false, true);
        // Select the delivery filter available on the URL request.
        if (this.el.querySelector(`#${params.get('delivery-filter-options')}-input`)) this.el.querySelector(`#${params.get('delivery-filter-options')}-input`).checked = true;
        this.#appliedDeliveryFilter = params.get('delivery-filter-options');

        // Go through every selected stores.
        for (const store of params.getAll('filter.v.m.custom.stores')) {
            const storeNumber = this.el.querySelector(`#Filter-Delivery-${store.replace('gid://shopify/Metaobject/', '')}`)?.value;

            if (!storeNumber) continue;

            // Make sure that the selected store appears as selected on the frontend (on page reload)
            if (params.get('delivery-filter-options') === CC_FILTER) this.el.querySelector(`#Filter-Container-Delivery-${storeNumber}`).style.display = 'flex';
            this.el.querySelector(`#Filter-Delivery-${storeNumber}`).checked = true;
        }

        // Get the latest list of available stores for the postcode.
        const deliveryStores = await processDeliveryFilter(this.#appliedDeliveryFilter, this.#postcode);

        document.querySelector(`#Filter-Delivery-Available-Stores`).value = deliveryStores.toString();

        await this.onCheckboxChanged(false);
    }

    /**
     * Unselects and hide all
     *
     * @return {*}
     */
    unselectAllDeliveryMethods() {
        document.querySelectorAll(`.filter-v-m-custom-stores`).forEach(storeField => {
            storeField.style.display = 'none';
            storeField.querySelector(`input[name="filter.v.m.custom.stores"]`).checked = false;
        });
    }

    /**
     * Handles postcode entry.
     * @returns {Promise<void>}
     */
    async onPostcodeEntry(invokeCheckboxChangedEvent = true) {
        // Provided value is a numeric value.
        const currentPostcode = this.#postcode;
        this.#postcode = this.el.querySelector('input[name="postcode-field"]')?.value;

        if (currentPostcode === this.#postcode) return;

        // Remove error message available on the available filters
        [SDD_FILTER, CC_FILTER].forEach(method => handleDeliveryErrorMessage(method, false))

        // Make sure that the given postcode is a numeric value.
        if (isNaN(Number(this.#postcode))) {
            this.#postcode = '';
            Shopify.queryParams['postcode-field'] = undefined;
            toggleDeliveryFilter(true, true);
            this.unselectAllDeliveryMethods();
            return;
        }

        const postcodeLength = this.#postcode?.toString().length;

        if (postcodeLength && postcodeLength !== 4) {
            // Invalid postcode length.
            toggleDeliveryFilter(true, postcodeLength > 4);
            this.unselectAllDeliveryMethods();
            return;
        }

        // Process valid postcode entry
        toggleDeliveryFilter(false, true);
        Shopify.queryParams['postcode-field'] = this.#postcode;
        // Confirm if shipping method has been selected or not.
        const shippingMethod = document.querySelector(`input[name="delivery-filter-options"]:checked`)?.value;

        if ([SDD_FILTER, CC_FILTER].includes(shippingMethod)) {
            await this.onShippingMethodSelection(true);

            return;
        }

        if (invokeCheckboxChangedEvent) await this.onCheckboxChanged(false);
    }

    /**
     * Handles filter operation when the selection method is selected by the user.
     *
     * @returns {Promise<void>}
     */
    async onShippingMethodSelection(postcodeChange = false) {
        if (!this.#postcode.length) return;

        let shippingMethod;

        // Get the delivery filter select option elements.
        const ccFilterInput = this.el.querySelector('#cc-filter-input');
        const sddFilterInput = this.el.querySelector('#sdd-filter-input');

        // Identify the selected delivery filter option.
        if (ccFilterInput && ccFilterInput.checked) {
            shippingMethod = CC_FILTER;
        } else if (sddFilterInput && sddFilterInput.checked) {
            shippingMethod = SDD_FILTER;
        }

        if (!shippingMethod) {
            console.error('Cannot identify the selected shipping method.');
            return;
        }

        // The selected filter has already been applied to the collections page.
        if (this.#appliedDeliveryFilter === shippingMethod && !postcodeChange) return;

        // If there already is an existing filter applied to the collections page then reset the selected store list.
        if (this.#appliedDeliveryFilter.length) resetStoresSelection();

        this.#appliedDeliveryFilter = shippingMethod;

        // Process the delivery filter event.
        const deliveryStores = await processDeliveryFilter(shippingMethod, this.#postcode);

        document.querySelector(`#Filter-Delivery-Available-Stores`).value = deliveryStores.toString();

        if (deliveryStores.length && shippingMethod === SDD_FILTER) return await this.onCheckboxChanged();
        await this.onCheckboxChanged(false);

        // Handle error messaging for unavailable stores
        handleDeliveryErrorMessage(shippingMethod, !deliveryStores.length);

        const allAvailableStores = document.querySelector(`#Filter-Delivery-Available-Stores`)?.value;

        if (allAvailableStores) Shopify.queryParams['filter.available.stores'] = allAvailableStores;
    }

    async onFormSubmit(event) {
        event.preventDefault();
        Woolman.Collection.classList.add('processing');
        Woolman.Utils.prepareQueryParams();
        generateSearchParamsFromFormData(this.el);

        Woolman.ModalsAndDrawers.closeModalOrDrawer(this.closest('[data-drawer]').getAttribute('id'));
        getCollectionAJAX(Object.values(this.selectors));
    }

    async onCheckboxChanged(setProcessing = true) {
      if (setProcessing) Woolman.Collection.classList.add('processing');
      generateSearchParamsFromFormData(this.el);

      // Check if we're on the search template
      if (document.documentElement.classList.contains('template-search')) {
        location.search = getCollectionURL();
      } else {
        getCollectionAJAX(Object.values(this.selectors));
      }
    }
}

if (customElements.get('facets-form') === undefined) {
    customElements.define('facets-form', FacetsForm);
}

class CurrentFacetButton extends HTMLElement {
    constructor() {
        'use strict';
        super();

        this.selectors = {
            grid: '#product-grid-ajax',
            count: '#products-count-ajax',
            currentFacets: '#current-facets-ajax',
            pagination: '#pagination-ajax',
        };

        this.btn = this.querySelector('a');
        this.btn.addEventListener('click', this.removeFacet.bind(this));
    }

    async removeFacet(event) {
        event.preventDefault();

        const filterParamName = event?.currentTarget?.dataset?.filterParamName;

        if (filterParamName === 'filter.v.price') {
            const price_slider = document.querySelector('price-range');

            delete Shopify.queryParams['filter.v.price.lte'];
            delete Shopify.queryParams['filter.v.price.gte'];

            price_slider.dispatchEvent(new Event('reset'));
            price_slider.removeAttribute('data-updated');
            getCollectionAJAX(Object.values(this.selectors));
        } else if (filterParamName === 'filter.v.m.custom.stores') {
            this.removeDeliveryFilterFacets(filterParamName, event.currentTarget.dataset.filterValue);
        } else {
            const inputToReset = document.querySelector(`#facets [data-key-value="${event.currentTarget.dataset.filterParamName}:${event.currentTarget.dataset.filterValue}"]`);

            if (!inputToReset) return;

            inputToReset.checked = false;
            inputToReset.dispatchEvent(new Event('change'));
        }
    }

    /**
     * Removes the selection of the delivery store that matches the name and value of the given element.
     */
    removeDeliveryFilterFacets(filterParamName, filterValue) {
        if (!filterParamName || !filterValue) return;

        const appliedDeliveryFilter = Shopify.queryParams['delivery-filter-options'];

        // Reset the filter that's removed from the list of applied filter options.
        const inputToReset = document.querySelector(`input[name="${filterParamName}"][value="${filterValue}"]`);
        inputToReset.checked = false;
        inputToReset.dispatchEvent(new Event('change'));

        // For SDD filter, if all stores are unselected, remove the delivery filter selection.
        if (appliedDeliveryFilter === SDD_FILTER) {
            const candidateStores = document.querySelectorAll(`input[name="${filterParamName}"`);

            let selectedStoreCount = 0;

            for (const store of candidateStores) {
                if (store.checked && store?.value !== filterValue) selectedStoreCount++;
            }

            if (selectedStoreCount === 0) {
                delete Shopify.queryParams['delivery-filter-options'];
                delete Shopify.queryParams['filter.available.stores'];

                document.querySelector('#sdd-filter-input').checked = false;
            }
        }
    }
}

if (customElements.get('current-facet-button') === undefined) {
    customElements.define('current-facet-button', CurrentFacetButton);
}